import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  Download,
  Mail,
  Smartphone,
  Sparkles,
} from 'lucide-react';

const GUIDE_URL = '/free-guides/3-day-macro-starter-plan.html';

const encode = (data) => new URLSearchParams(data).toString();

export default function LeadMagnetPage() {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Free 3-Day Macro Starter Plan | Integrative Health Journal';
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setStatus('submitting');
    setError('');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'macro-starter-plan',
          ...form,
          source: 'free-macro-starter-plan',
        }),
      });

      if (!response.ok) {
        throw new Error('Signup failed. Please try again.');
      }

      setStatus('success');
    } catch (err) {
      setStatus('idle');
      setError(err.message || 'Signup failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f3ea] text-[#1f2a22]">
      <header className="border-b border-[#ded2bf] bg-[#fbf7ef]/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#c9b99d]" />
            <span className="font-serif text-lg font-semibold">Integrative Health Journal</span>
          </Link>
          <Link to="/books/macros-made-easy-for-women" className="hidden rounded-full border border-[#b9a98f] px-4 py-2 text-sm font-bold text-[#334033] hover:bg-[#efe4d2] sm:inline-flex">
            View bundle
          </Link>
        </div>
      </header>

      <main>
        <section className="px-4 py-14 sm:px-6 lg:py-20">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 border-y border-[#c9b99d] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#6f785f]">
                <Sparkles className="h-4 w-4" />
                Free macro starter plan
              </div>
              <h1 className="font-serif text-5xl font-semibold leading-[0.98] tracking-normal text-[#182018] sm:text-6xl">
                Learn macros without diet rules.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[#596154]">
                Get a practical 3-day macro starter plan for women: simple meal structure, high-protein recipe ideas, tracking prompts, and a clean path into the full Macros Made Easy system.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  { icon: CheckCircle, text: 'Beginner-friendly macro structure' },
                  { icon: BookOpen, text: 'Sample meals and recipes' },
                  { icon: Smartphone, text: 'Designed to pair with the IHJ app' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.text} className="rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] p-4">
                      <Icon className="h-5 w-5 text-[#536343]" />
                      <p className="mt-2 text-sm font-semibold leading-5 text-[#334033]">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[2rem] border border-[#d8cbb8] bg-[#fbf7ef] p-5 shadow-xl shadow-[#9b8d73]/10">
              {status === 'success' ? (
                <div className="p-4 text-center sm:p-8">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e5edd8] text-[#536343]">
                    <CheckCircle className="h-7 w-7" />
                  </div>
                  <h2 className="mt-5 font-serif text-3xl font-semibold text-[#1f2a22]">Your starter plan is ready.</h2>
                  <p className="mt-3 text-sm leading-6 text-[#626b5d]">
                    Open it now, then save or print it from your browser.
                  </p>
                  <a
                    href={GUIDE_URL}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white hover:bg-[#435236]"
                  >
                    Open the free guide <Download className="h-4 w-4" />
                  </a>
                  <Link to="/books/macros-made-easy-for-women" className="mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-[#b9a98f] px-6 py-3 text-sm font-bold text-[#334033] hover:bg-[#efe4d2]">
                    See the complete bundle <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <form name="macro-starter-plan" method="POST" data-netlify="true" onSubmit={submit} className="space-y-4">
                  <input type="hidden" name="form-name" value="macro-starter-plan" />
                  <input type="hidden" name="source" value="free-macro-starter-plan" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#7b806f]">Instant access</p>
                    <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1f2a22]">Get the free guide</h2>
                    <p className="mt-2 text-sm leading-6 text-[#626b5d]">
                      No payment. No diet challenge. Just the starter plan and practical IHJ updates.
                    </p>
                  </div>

                  <label className="block">
                    <span className="text-sm font-bold text-[#334033]">First name</span>
                    <input
                      name="name"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="mt-2 w-full rounded-xl border border-[#c9b99d] bg-white px-4 py-3 text-[#1f2a22] outline-none focus:border-[#536343] focus:ring-2 focus:ring-[#536343]/20"
                      placeholder="Your name"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-[#334033]">Email</span>
                    <input
                      required
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="mt-2 w-full rounded-xl border border-[#c9b99d] bg-white px-4 py-3 text-[#1f2a22] outline-none focus:border-[#536343] focus:ring-2 focus:ring-[#536343]/20"
                      placeholder="you@example.com"
                    />
                  </label>

                  {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#536343] px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-[#435236] disabled:opacity-70"
                  >
                    {status === 'submitting' ? 'Sending...' : 'Send me the free plan'}
                    <Mail className="h-4 w-4" />
                  </button>

                  <p className="text-xs leading-5 text-[#7b806f]">
                    By submitting, you agree to receive Integrative Health Journal emails. You can unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="border-y border-[#d8cbb8] bg-[#ede3d3] px-4 py-10 sm:px-6">
          <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-4">
            {[
              ['Day 1', 'Build a high-protein baseline without overhauling your life.'],
              ['Day 2', 'Learn how carbs and fats fit into flexible meals.'],
              ['Day 3', 'Use the tracker, review patterns, and choose the next step.'],
              ['Next', 'Upgrade to the full book + app bundle when ready.'],
            ].map(([title, text]) => (
              <div key={title} className="rounded-2xl border border-[#d8cbb8] bg-[#fbf7ef] p-5">
                <h3 className="font-serif text-2xl font-semibold text-[#1f2a22]">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-[#626b5d]">{text}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
